import React, { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ChevronDownIcon, CloseIcon } from '../../assets/icons/index';
import smilingFaceImage from '../../assets/images/smiling-face.png';
import { GetStarted } from '../../components/activity-bar/get-started';
import { AppFormattedMessage } from '../../components/app-formatted-message/index';
import Button from '../../components/button/index';
import Loader from '../../components/loader/index';
import { useSelectedCompany } from '../../hooks/index';
import { showOnboardingPageState } from '../../storage/local/index';
import { PageRoute } from '../../types/page.types';
import { StakeholderRole } from '../../types/stakeholder.types';

const Onboarding: FC = () => {
  const navigate = useNavigate();
  const { selectedCompany, isLoading, refetch } = useSelectedCompany();
  const [isRefetching, setIsRefetching] = useState(true);
  const role = selectedCompany?.roleInCompany;

  const handleClose = () => {
    showOnboardingPageState.set(false);
    if (role === StakeholderRole.STAKEHOLDER) {
      navigate(PageRoute.MY_HOLDINGS);
    } else {
      navigate(PageRoute.DASHBOARD);
    }
  };

  useEffect(() => {
    const timeout = setTimeout(() => {
      refetch().then(() => {
        setIsRefetching(false);
      });
    }, 1000);

    return () => clearTimeout(timeout);
  }, [refetch]);

  if (isLoading || isRefetching) return <Loader />;

  return (
    <div className="flex h-full w-full flex-col items-center justify-center gap-3 overflow-y-auto">
      <div className="relative flex w-full max-w-[442px] flex-col items-center justify-center rounded-xl border-[1px] border-[#f2f2f2] p-6 shadow-buttonShadow">
        <Button
          className="absolute right-4 top-4 size-9 rounded-full shadow-md transition-colors hover:bg-gray-50"
          onClick={handleClose}
          styleType="NONE"
        >
          <CloseIcon />
        </Button>
        <span
          className="max-w-[380px] truncate !bg-clip-text text-4xl-mobile font-bold text-transparent lg:text-4xl"
          style={{
            background: 'linear-gradient(132.59deg, #475467 29.58%, #101828 84.27%)',
          }}
        >
          <AppFormattedMessage id="WELCOME_ABOARD" />,
        </span>
        <span className="mt-2 text-sm font-[450] text-gray-600">
          Let’s get started with your CapQuest
        </span>
        {role === StakeholderRole.ADMIN && (
          <GetStarted
            companyId={selectedCompany?.id || ''}
            isOnboardingPage
            isRefetching={isRefetching}
          />
        )}
      </div>
      <div className="flex w-full max-w-[442px] items-center justify-between rounded-xl border-[1px] border-[#f2f2f2] bg-white px-6 py-3 shadow-buttonShadow">
        <div className="flex cursor-pointer items-center gap-4">
          <div className="flex !size-10 shrink-0 items-center justify-center rounded-lg border-[1px] border-gray-200">
            <img alt="" className="size-[20px]" src={smilingFaceImage} />
          </div>
          <a
            className="flex flex-col gap-2 text-start"
            href={process.env.REACT_APP_SCHEDULE_CALL_URL}
            rel="noreferrer noopener nofollow"
            target="_blank"
          >
            <span className="text-sm font-[550] text-[#172335]">
              <AppFormattedMessage id="HELP_ME_SETUP" />
            </span>
            <span className="text-label-md font-normal text-gray-600">
              Schedule a call, and we'll help you with your setup!
            </span>
          </a>
        </div>
        <Button
          className="h-fit w-fit -rotate-90 rounded p-1 transition-colors hover:bg-gray-100"
          styleType="NONE"
        >
          <ChevronDownIcon className="size-3" iconColor="#172335" />
        </Button>
      </div>
    </div>
  );
};

export default Onboarding;
